import { useEffect, useMemo, useState } from 'react';
import type {
  ApiClient, AussenNachweisBauwerk, AussenNachweisBefunde,
  AussenNachweisHydrant, AussenNachweisHydranten,
  AussenNachweisStrang, AussenNachweisStraenge,
  AussenNachweisVersickerung, AussenStrangzeile, AussenTeilstrecke,
} from '../api';
import { bauartname, bereichname, kreisname } from '../aussenKlartext';
import {
  Baum, ContentSection, DataTable, EmptyState, ErrorState, FilterBar, LoadingState, PropertyGrid, StatusBadge,
  formatiereZahl, useFilterInUrl, type BaumKnoten, type FilterDefinition, type Spalte,
} from '../muster';
import { Rechenstand, aussenFachvorbehalt } from './sanitaer/kopf';
import {
  NachweisRechenstand, OHNE_LAUF, berichtZiel, bewertungStatus, kfText, nochNichtGerechnet, useAussenKopf,
  useBerichtsstand,
} from './aussenanlagen/kopf';
import './aussenanlagen/aussen.css';

/**
 * Der Nachweis der Außenanlagen — Bereich, Strang, Teilstrecke.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM NEBEN DER SEITE „BERECHNUNGEN“ NOCH DIESE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `AussenBerechnung.tsx` zeigt EINEN Lauf, so wie er abgelegt ist: 112 Karten
 * untereinander, jede mit ihrer Teilstreckentabelle. Das ist die richtige
 * Darstellung, um zu sehen, was gerechnet wurde.
 *
 * Es ist die falsche, um eine Frage zu beantworten. „Welche Stränge des
 * Regenwassers sind beanstandet, und warum?“ heißt dort: 112 Karten
 * durchblättern. Der Baum links beantwortet sie in zwei Klicks, und rechts
 * steht der Grund im Klartext statt als Schlüsselwort.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DER GRUND STEHT NEBEN DER BEWERTUNG, NICHT DARUNTER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 238 der 282 Teilstrecken tragen die Bewertung `ohne_abfluss`. Das ist kein
 * Mangel an der Leitung, sondern eine Nachweislücke — und wer sie für einen
 * Mangel hält, färbt das ganze Projekt rot und findet die neun echten
 * Gefällebefunde nicht mehr.
 *
 * Deshalb hat jede Zeile eine Spalte „Grund“. Bei `ohne_abfluss` steht dort,
 * WARUM kein Bemessungsabfluss zu bilden war — nicht „—“.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE FALLSTÜCKE SIND EIGENS GEKENNZEICHNET (Befund A-14)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 65 Teilstrecken stehen steiler als 45°. Für sie sind Vollstrom und
 * Mindestgefälle beide sinnlos, und bis zum 16.09.2026 verdarben sie das
 * mittlere Gefälle ihres Strangs: RWSP 71 stand mit 947,4 ‰ in der Liste,
 * verlegt sind 9,8 ‰.
 *
 * Sie stehen weiter in der Tabelle — verschwiegen wäre schlechter als falsch
 * gemittelt —, aber mit der Marke „Fallstück“ und außerhalb des Mittels.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * TS9b1b (TD3, 26.10.2026): TX-STANDARD S1
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Seite ist ein Reiter der Außenanlagen (E1): Rechenstand des
 * Freispiegellaufs, Exportleiste (PDF-Bericht und Excel Freispiegel) und der
 * Vorbehalt AUA-22 gehen an den Kopf des Rahmens. Der Baum ist das Muster
 * `Baum` (ohne eigene Kappung), alle Listen sind `DataTable`, die Lage der
 * Hydranten trägt `role="img"` mit einer Beschreibung und ihre Werte stehen
 * als Tabelle daneben (S1.10).
 */

/*
 * KREISNAMEN und BEWERTUNG standen bis zum 23.09.2026 hier als eigene
 * Teillisten: DREI Kreise, waehrend die Leitungen sieben tragen, und
 * vierzehn Bewertungen, waehrend die Rechnung am 23.09.2026 vierzehn
 * VERSCHIEDENE schrieb - darunter 'ohne_schacht' (135 Trassen),
 * 'weg_unvollstaendig', 'abseits', 'nicht_pruefbar' und 'drossel_offen',
 * die in keiner der beiden Listen standen. Sie fielen auf den Rohwert und
 * standen als Datenbankschluessel auf dem Blatt.
 *
 * Beides kommt jetzt aus `../aussenKlartext`; die Dreiteilung ok / Lücke /
 * Befund als Statusmarke aus `bewertungStatus` (wie auf der Berechnungsseite).
 */

/** Ein Feld aus den Werten eines Laufs - der Server schreibt die Schlüssel mal groß, mal klein. */
function feld(werte: Record<string, unknown> | null | undefined, name: string): unknown {
  const w = werte ?? {};
  return w[name] ?? w[name.charAt(0).toLowerCase() + name.slice(1)];
}

/** Dasselbe als Zahl; alles andere ist eine Fehlstelle. */
function zahlfeld(werte: Record<string, unknown> | null | undefined, name: string): number | null {
  const v = feld(werte, name);
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

const marke = (bewertung: string | null | undefined) => <StatusBadge {...bewertungStatus(bewertung)} klein />;

const BAUMFILTER: FilterDefinition[] = [
  { schluessel: 'strang', beschriftung: 'Strang suchen', art: 'text', platzhalter: 'Strang' },
  { schluessel: 'befund', beschriftung: 'nur Stränge mit Bewertung ≠ ok', art: 'haken' },
];

export function AussenNachweis({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [straenge, setStraenge] = useState<AussenNachweisStraenge | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);
  const [rigolen, setRigolen] =
    useState<AussenNachweisVersickerung | null>(null);
  const [hydranten, setHydranten] =
    useState<AussenNachweisHydranten | null>(null);
  const [befunde, setBefunde] = useState<AussenNachweisBefunde | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);

  useEffect(() => {
    setFehler(null);
    api.aussenNachweisStraenge(projektId)
      .then(setStraenge)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId, versuch]);

  useEffect(() => {
    // Ohne Lauf des Bereichs gibt es diese Abschnitte nicht - kein Fehler der Seite.
    api.aussenNachweisVersickerung(projektId).then(setRigolen).catch(() => setRigolen(null));
    api.aussenNachweisHydranten(projektId).then(setHydranten).catch(() => setHydranten(null));
    api.aussenNachweisBefunde(projektId).then(setBefunde).catch(() => setBefunde(null));
  }, [api, projektId]);

  const stand = useBerichtsstand(api, projektId, 'freispiegel');
  const bericht = berichtZiel(api, projektId, 'freispiegel', stand);
  const frage = straenge ? aussenFachvorbehalt('freispiegel') : null;

  useAussenKopf({
    meta: straenge
      ? <NachweisRechenstand lauf={{ id: straenge.id, norm: straenge.norm, gerechnetAm: straenge.gerechnetAm }} />
      : fehler ? <Rechenstand lauf={null} /> : undefined,
    ziele: [
      straenge ? bericht : { ...bericht, gesperrt: { grund: OHNE_LAUF } },
      {
        art: 'excel', beschriftung: 'Excel Freispiegel',
        laden: () => api.aussenBerechnungExcel(projektId, 'freispiegel'),
        gesperrt: straenge ? undefined : { grund: OHNE_LAUF },
      },
    ],
    vorbehalte: frage ? [frage] : [],
  }, [api, projektId, straenge, fehler, stand]);

  if (fehler) {
    return nochNichtGerechnet(fehler) ? (
      <EmptyState
        titel="Für die Außenanlagen wurde noch nicht gerechnet."
        erklaerung="Der Nachweis zeigt den jüngsten Lauf der Freispiegelentwässerung — unter „Berechnungen“ auf „neu rechnen“."
      />
    ) : (
      <ErrorState
        titel="Der Nachweis ließ sich nicht laden."
        meldung="Das ist ein Fehler und kein Ergebnis - die Rechnung selbst ist davon nicht berührt."
        technisch={fehler}
        beiErneutVersuchen={() => setVersuch((v) => v + 1)}
      />
    );
  }

  if (!straenge) return <LoadingState text="Nachweis der Freispiegelentwässerung wird abgerufen …" />;

  return (
    <>
      <Kennwerte straenge={straenge} />

      {befunde && befunde.befunde.length > 0 && <Befundliste befunde={befunde} />}

      <ContentSection
        titel="Stränge"
        beschreibung="Links einen Kreis aufklappen und einen Strang wählen; rechts stehen seine Teilstrecken mit dem Grund jeder Bewertung."
      >
        <div className="aus-zweispalter">
          <Strangbaum straenge={straenge} gewaehlt={gewaehlt} waehlen={setGewaehlt} />
          <Strangseite api={api} projektId={projektId} gewaehlt={gewaehlt} />
        </div>
      </ContentSection>

      {rigolen && <Rigolentabelle daten={rigolen} />}

      {hydranten && <Hydrantenkarte daten={hydranten} />}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Kennwerte des Freispiegellaufs und die Kreise
// ═══════════════════════════════════════════════════════════════════════════

type Kreiszeile = AussenNachweisStraenge['kreise'][number];

const KREISSPALTEN: Spalte<Kreiszeile>[] = [
  { schluessel: 'kreis', titel: 'Kreis', immerSichtbar: true, wert: (k) => kreisname(k.kreis) },
  { schluessel: 'straenge', titel: 'Stränge', art: 'zahl', nachkomma: 0, fuss: 'summe' },
  { schluessel: 'laengeM', titel: 'Länge', einheit: 'm', art: 'zahl', nachkomma: 1, fuss: 'summe' },
  { schluessel: 'beanstandet', titel: 'beanstandet', art: 'zahl', nachkomma: 0, fuss: 'summe' },
  { schluessel: 'ohneAbfluss', titel: 'ohne Abfluss', art: 'zahl', nachkomma: 0, fuss: 'summe' },
];

/**
 * Die Kennwerte stehen als Tafel und nicht als zweite Kennzahlenreihe: Die
 * Reihe über den Reitern gehört dem Bestand (S1.2 Nr. 3, höchstens sechs).
 * Befund A-14: „davon Fallstücke“ steht dabei - ohne die Zahl sieht das
 * bereinigte Mittel aus wie ein unveränderter Wert.
 */
function Kennwerte({ straenge }: { straenge: AussenNachweisStraenge }) {
  const k = straenge.kennzahlen;

  return (
    <ContentSection titel="Freispiegelentwässerung" beschreibung={straenge.norm ?? undefined}>
      <PropertyGrid
        spalten={3}
        dicht
        eintraege={[
          { beschriftung: 'Stränge', wert: zahlfeld(k, 'Straenge'), nachkomma: 0 },
          { beschriftung: 'Teilstrecken', wert: zahlfeld(k, 'Gerechnet'), nachkomma: 0 },
          { beschriftung: 'Länge', wert: zahlfeld(k, 'LaengeGesamtM'), einheit: 'm', nachkomma: 1 },
          { beschriftung: 'mit Bemessungsabfluss', wert: zahlfeld(k, 'TeilstreckenMitAbfluss'), nachkomma: 0 },
          { beschriftung: 'Stränge beanstandet', wert: zahlfeld(k, 'StraengeBeanstandet'), nachkomma: 0 },
          {
            beschriftung: 'davon Fallstücke',
            wert: `${formatiereZahl(zahlfeld(k, 'Fallstuecke'), 0)} / ${formatiereZahl(zahlfeld(k, 'FallstueckeLaengeM'), 2)} m`,
          },
        ]}
      />

      <DataTable<Kreiszeile>
        name="Kreise"
        spalten={KREISSPALTEN}
        zeilen={straenge.kreise}
        schluessel={(kr) => kr.kreis}
        leer={<EmptyState klein titel="Der Lauf trägt keinen Strang." />}
        suche={false}
        dicht
      />
    </ContentSection>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Befunde
// ═══════════════════════════════════════════════════════════════════════════

type Befundzeile = AussenNachweisBefunde['befunde'][number];

const BEFUNDSPALTEN: Spalte<Befundzeile>[] = [
  { schluessel: 'bereich', titel: 'Bereich', wert: (b) => bereichname(b.bereich) },
  { schluessel: 'bezeichnung', titel: 'Fall', immerSichtbar: true, wert: (b) => b.bezeichnung },
  {
    schluessel: 'bewertung', titel: 'Bewertung', art: 'status',
    wert: (b) => bewertungStatus(b.bewertung).text, inhalt: (b) => marke(b.bewertung),
  },
  { schluessel: 'befund', titel: 'Grund', inhalt: (b) => <span className="aus-fein">{b.befund ?? '—'}</span> },
  {
    schluessel: 'rechenweg', titel: 'Rechenweg',
    wert: (b) => (b.rechenweg && b.rechenweg.length > 0 ? b.rechenweg.join(' · ') : null),
    inhalt: (b) => <span className="schwach aus-fein">{b.rechenweg?.join(' · ') || '—'}</span>,
  },
];

/**
 * Die Normbefunde stehen GANZ OBEN und über alle sechs Bereiche zusammen.
 *
 * Ein Befund, der nur im Bericht seines Bereichs steht, wird gefunden, wenn
 * man ihn schon sucht. Die beanstandeten Fälle stehen darunter als zweite
 * Gruppe, zugeklappt - statt hinter einem Knopf „N Fälle zeigen“.
 */
function Befundliste({ befunde }: { befunde: AussenNachweisBefunde }) {
  const echte = befunde.befunde.filter((b) => b.gattung === 'befund').length;
  const uebrige = befunde.befunde.length - echte;

  return (
    <ContentSection titel={`Befunde (${echte} Normbefunde, ${uebrige} beanstandete Fälle)`}>
      <DataTable<Befundzeile>
        name="Befunde über alle Bereiche"
        spalten={BEFUNDSPALTEN}
        zeilen={befunde.befunde}
        schluessel={(b) => b.id}
        gruppierung={{
          nach: (b) => (b.gattung === 'befund' ? 'befund' : 'fall'),
          titel: (g) => (g === 'befund' ? 'Normbefunde' : 'beanstandete Fälle'),
          ordnung: (a, b) => (a === b ? 0 : a === 'befund' ? -1 : 1),
          zugeklappt: ['fall'],
        }}
        ausfuhr
        ausfuhrDateiname="aussen-befunde"
        dicht
      />
    </ContentSection>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Der Baum
// ═══════════════════════════════════════════════════════════════════════════

type Strangknoten = { art: 'kreis' } | { art: 'strang'; strang: AussenStrangzeile };

/**
 * Kreis → Strang über das Muster `Baum` - ohne eigene Kappung; der erste Kreis
 * steht offen, weil ein Baum, der ganz zu ist, aussieht wie eine leere Seite.
 * Suche und „nur ≠ ok“ stehen in der Adresse (S1.3).
 */
function Strangbaum({ straenge, gewaehlt, waehlen }: {
  straenge: AussenNachweisStraenge;
  gewaehlt: string | null;
  waehlen: (id: string) => void;
}) {
  const [filter, setFilter] = useFilterInUrl(BAUMFILTER);
  const suche = (typeof filter.strang === 'string' ? filter.strang : '').trim().toLowerCase();
  const nurBefund = filter.befund === true;

  const gefiltert = straenge.straenge.filter((s) => {
    if (nurBefund && s.bewertung === 'ok') return false;
    return !suche || (s.bezeichnung ?? '').toLowerCase().includes(suche);
  });

  const knoten: BaumKnoten<Strangknoten>[] = useMemo(() => straenge.kreise.map((kr) => {
    const meine = gefiltert.filter((s) => (s.kreis ?? 'ohne Kreis') === kr.kreis);
    return {
      id: `k:${kr.kreis}`,
      titel: kreisname(kr.kreis),
      anzahl: meine.length,
      daten: { art: 'kreis' as const },
      marke: kr.beanstandet > 0
        ? <StatusBadge zustand="warnung" text={`${formatiereZahl(kr.beanstandet, 0)} beanstandet`} klein />
        : undefined,
      kinder: meine.map((s) => ({
        id: s.id,
        titel: s.bezeichnung ?? '—',
        daten: { art: 'strang' as const, strang: s },
        hinweis: s.istMassgebend ? 'maßgebend' : undefined,
        marke: marke(s.bewertung),
      })),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [straenge, suche, nurBefund]);

  const erster = straenge.kreise.length > 0 ? straenge.kreise[0] : null;

  return (
    <div>
      <FilterBar filter={BAUMFILTER} werte={filter} beiAenderung={setFilter} inLeiste={2} />
      <p className="aus-hinweis" data-strangzahl>
        {formatiereZahl(gefiltert.length, 0)} von {formatiereZahl(straenge.straenge.length, 0)} Strängen
      </p>
      <Baum<Strangknoten>
        name="Stränge je Kreis"
        knoten={knoten}
        gewaehlt={gewaehlt}
        anfangsOffen={erster ? [`k:${erster.kreis}`] : []}
        beiWahl={(k) => { if (k.daten?.art === 'strang') waehlen(k.daten.strang.id); }}
        hoehe={560}
        leer={(
          <EmptyState klein
            titel={straenge.straenge.length === 0 ? 'Der Lauf trägt keinen Strang.' : 'Kein Strang passt zum Filter.'}
            erklaerung={straenge.straenge.length === 0 ? undefined : 'Suche oder „nur ≠ ok“ zurücknehmen.'} />
        )}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Das Strangblatt
// ═══════════════════════════════════════════════════════════════════════════

/** Rechts neben dem Baum: nichts gewählt, lädt, Fehler oder das Blatt des Strangs. */
function Strangseite({ api, projektId, gewaehlt }: {
  api: ApiClient; projektId: string; gewaehlt: string | null;
}) {
  const [strang, setStrang] = useState<AussenNachweisStrang | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);

  useEffect(() => {
    setStrang(null);
    setFehler(null);
    if (!gewaehlt) return;

    let lebt = true;
    api.aussenNachweisStrang(projektId, gewaehlt)
      .then((s) => { if (lebt) setStrang(s); })
      .catch((e) => { if (lebt) setFehler(e instanceof Error ? e.message : String(e)); });
    return () => { lebt = false; };
  }, [api, projektId, gewaehlt, versuch]);

  if (!gewaehlt) {
    return (
      <EmptyState klein
        titel="Noch kein Strang gewählt."
        erklaerung={'Links einen Kreis aufklappen und einen Strang wählen. Rechts stehen dann seine Teilstrecken '
          + '— Nennweite, Länge, Gefälle, Bemessungsabfluss, Füllungsgrad und Fließgeschwindigkeit, und zu jeder '
          + 'Bewertung der Grund.'}
      />
    );
  }

  if (fehler) {
    return (
      <ErrorState klein
        titel="Die Teilstrecken dieses Strangs ließen sich nicht laden."
        technisch={fehler}
        beiErneutVersuchen={() => setVersuch((v) => v + 1)}
      />
    );
  }

  if (!strang) return <LoadingState text="Teilstrecken werden abgerufen …" />;

  return <Strangblatt strang={strang} />;
}

const TEILSTRECKENSPALTEN: Spalte<AussenTeilstrecke>[] = [
  { schluessel: 'abschnittNr', titel: 'Nr', art: 'zahl', nachkomma: 0, immerSichtbar: true },
  { schluessel: 'elementId', titel: 'Element', wert: (t) => String(t.elementId) },
  { schluessel: 'nennweite', titel: 'DN', art: 'zahl', nachkomma: 0 },
  { schluessel: 'innendurchmesserMm', titel: 'd_i', einheit: 'mm', art: 'zahl', nachkomma: 1 },
  { schluessel: 'laengeM', titel: 'L', einheit: 'm', art: 'zahl', nachkomma: 2, fuss: { art: 'summe', schranke: true } },
  {
    schluessel: 'gefaellePromille', titel: 'J', einheit: '‰', art: 'zahl', nachkomma: 2,
    inhalt: (t) => (
      <>
        {formatiereZahl(t.gefaellePromille, 2)}
        {t.istFallstueck && <>{' '}<StatusBadge zustand="info" text="Fallstück" klein /></>}
      </>
    ),
  },
  { schluessel: 'mindestgefaelleSoll', titel: 'Soll', einheit: '‰', art: 'zahl', nachkomma: 1 },
  { schluessel: 'abflussLs', titel: 'Q̇', einheit: 'l/s', art: 'zahl', nachkomma: 2 },
  { schluessel: 'fuellungsgrad', titel: 'h/d_i', art: 'zahl', nachkomma: 3 },
  { schluessel: 'geschwindigkeitMs', titel: 'v', einheit: 'm/s', art: 'zahl', nachkomma: 2 },
  { schluessel: 'vollstromLs', titel: 'Q̇_voll', einheit: 'l/s', art: 'zahl', nachkomma: 2 },
  { schluessel: 'vollstromGeschwindigkeitMs', titel: 'v_voll', einheit: 'm/s', art: 'zahl', nachkomma: 2 },
  {
    schluessel: 'bewertung', titel: 'Bewertung', art: 'status',
    wert: (t) => bewertungStatus(t.bewertung).text, inhalt: (t) => marke(t.bewertung),
  },
  { schluessel: 'befund', titel: 'Grund', inhalt: (t) => <span className="aus-fein">{t.befund ?? '—'}</span> },
];

function Strangblatt({ strang }: { strang: AussenNachweisStrang }) {
  const w = strang.werte;
  const teile = strang.teilstrecken ?? [];

  const fallstuecke = zahlfeld(w, 'Fallstuecke') ?? 0;
  const fallLaenge = zahlfeld(w, 'FallstueckeLaengeM') ?? 0;

  return (
    <div data-strangblatt>
      <h3>
        {strang.bezeichnung ?? '—'}
        <span className="schwach"> · {kreisname(strang.kreis)}</span>{' '}
        {marke(strang.bewertung)}
      </h3>

      <PropertyGrid
        spalten={3}
        dicht
        eintraege={[
          { beschriftung: 'Teilstrecken', wert: zahlfeld(w, 'Teilstrecken'), nachkomma: 0 },
          { beschriftung: 'Länge', wert: zahlfeld(w, 'LaengeM'), einheit: 'm', nachkomma: 2 },
          { beschriftung: 'Gefälle verlegt', wert: zahlfeld(w, 'GefaelleMittel'), einheit: '‰', nachkomma: 2 },
          {
            beschriftung: 'davon Fallstücke',
            wert: `${formatiereZahl(fallstuecke, 0)} / ${formatiereZahl(fallLaenge, 2)} m`,
          },
          { beschriftung: 'Q̇ Bemessung', wert: zahlfeld(w, 'AbflussLs'), einheit: 'l/s', nachkomma: 2 },
          { beschriftung: 'A_u', wert: zahlfeld(w, 'AuM2'), einheit: 'm²', nachkomma: 1 },
        ]}
      />

      {fallstuecke > 0 && (
        <p className="aus-hinweis">
          <StatusBadge zustand="info" text="Mittel ohne Fallstücke" klein />{' '}
          Das mittlere Gefälle steht OHNE die {fallstuecke} Fallstücke. Sie sind
          steiler als 45° und damit keine verlegte Grundleitung. Bis zum
          16.09.2026 gingen sie mit ins Mittel — ein Strang, dessen verlegter
          Teil waagerecht liegt, stand dann mit mehreren hundert Promille in der
          Liste (Befund A-14).
        </p>
      )}

      {strang.befund && (
        <p className="aus-hinweis">
          <StatusBadge {...bewertungStatus(strang.bewertung)} klein />{' '}{strang.befund}
        </p>
      )}

      <DataTable<AussenTeilstrecke>
        name="Teilstrecken"
        spalten={TEILSTRECKENSPALTEN}
        zeilen={teile}
        schluessel={(t) => String(t.abschnittNr)}
        zeilenKlasse={(t) => (t.bewertung !== 'ok' && t.bewertung !== 'ohne_abfluss' ? 'aus-zeile-warnung' : undefined)}
        leer={<EmptyState klein titel="Der Strang trägt keine Teilstrecke." />}
        ausfuhr
        ausfuhrDateiname={`aussen-nachweis-${strang.bezeichnung ?? 'strang'}`}
        dicht
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Rigolen
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Deckung ist das Verhältnis des GEBAUTEN zum erforderlichen Volumen.
 * Sie ist dreiwertig und nicht zweiwertig: Unterdeckt ist ein Mangel,
 * doppelt so groß gebaut ist einer der anderen Art — entweder ist die
 * Zuordnung Bauwerk/Bemessungsbericht vertauscht oder das Bauwerk ist falsch.
 */
function deckung(d: number | null): { klasse?: string; marke?: { zustand: 'fehler' | 'warnung'; text: string } } {
  if (d === null) return {};
  if (d < 100) return { klasse: 'aus-zeile-fehler', marke: { zustand: 'fehler', text: 'unterdeckt' } };
  if (d > 190) return { klasse: 'aus-zeile-warnung', marke: { zustand: 'warnung', text: 'überdimensioniert' } };
  return {};
}

const RIGOLENSPALTEN: Spalte<AussenNachweisBauwerk>[] = [
  { schluessel: 'bezeichnung', titel: 'Bauwerk', immerSichtbar: true, wert: (b) => b.bezeichnung },
  { schluessel: 'bauart', titel: 'Bauart', wert: (b) => bauartname(b.bereich) },
  { schluessel: 'au', titel: 'A_u', einheit: 'm²', art: 'zahl', nachkomma: 1, wert: (b) => zahlfeld(b.werte, 'AuM2') },
  {
    schluessel: 'kf', titel: 'k_f', einheit: 'm/s', ausrichtung: 'rechts',
    wert: (b) => zahlfeld(b.werte, 'KfWert'), inhalt: (b) => kfText(zahlfeld(b.werte, 'KfWert'), 2),
  },
  {
    schluessel: 'bericht', titel: 'V Bericht', einheit: 'm³', art: 'zahl', nachkomma: 2,
    wert: (b) => zahlfeld(b.werte, 'ErforderlichM3'),
  },
  {
    schluessel: 'bisher', titel: 'V bisher', einheit: 'm³', art: 'zahl', nachkomma: 2,
    wert: (b) => zahlfeld(b.werte, 'NachgerechnetM3'), fuss: { art: 'summe', schranke: true },
  },
  {
    schluessel: 'dwa', titel: 'V DWA-A 138', einheit: 'm³', art: 'zahl', nachkomma: 2,
    wert: (b) => zahlfeld(b.werte, 'NachDwaA138M3'), fuss: { art: 'summe', schranke: true },
  },
  {
    schluessel: 'dauer', titel: 'D maßg.', einheit: 'min', art: 'zahl', nachkomma: 0,
    wert: (b) => zahlfeld(b.werte, 'DauerMassgebendMin'),
  },
  {
    schluessel: 'modell', titel: 'V Modell', einheit: 'm³', art: 'zahl', nachkomma: 2,
    wert: (b) => zahlfeld(b.werte, 'VolumenModellM3'),
  },
  {
    schluessel: 'deckung', titel: 'Deckung', einheit: '%', art: 'zahl', nachkomma: 1,
    wert: (b) => zahlfeld(b.werte, 'DeckungModellProzent'),
    inhalt: (b) => {
      const d = zahlfeld(b.werte, 'DeckungModellProzent');
      const m = deckung(d).marke;
      return <>{formatiereZahl(d, 1)}{m && <>{' '}<StatusBadge {...m} klein /></>}</>;
    },
  },
  {
    schluessel: 'bewertung', titel: 'Bewertung', art: 'status',
    wert: (b) => bewertungStatus(b.bewertung).text, inhalt: (b) => marke(b.bewertung),
  },
  { schluessel: 'befund', titel: 'Grund', inhalt: (b) => <span className="aus-fein">{b.befund ?? '—'}</span> },
];

/**
 * Beide Rechenwege nebeneinander und die DECKUNG bewertet.
 *
 * Der bisherige Rechenweg und der nach DWA-A 138 mit f_Z, k_f/2, A_u + A_s
 * und der Iteration über alle 22 Dauerstufen liefern verschiedene Zahlen —
 * über die dreizehn bemessenen Rigolen 685 gegen 1.216 m³. Wer nur eine der
 * beiden sieht, hält sie für DIE Zahl.
 */
function Rigolentabelle({ daten }: { daten: AussenNachweisVersickerung }) {
  return (
    <ContentSection
      titel={`Versickerung (${daten.bauwerke.length} Bauwerke)`}
      beschreibung={daten.norm ?? undefined}
    >
      <p className="aus-hinweis">
        <strong>V bisher</strong> ist der Rechenweg, wie er bis zum 16.09.2026
        umgesetzt war; <strong>V nach DWA-A 138</strong> derselbe mit
        Zuschlagsfaktor f_Z, halbiertem k_f, A_u + A_s und der Iteration über
        alle Dauerstufen. <strong>Deckung</strong> ist das GEBAUTE Volumen
        (Modell × Speicherziffer) gegen das erforderliche — „unterdeckt“ unter
        100 %, „überdimensioniert“ über 190 %.
      </p>

      <DataTable<AussenNachweisBauwerk>
        name="Rigolen"
        spalten={RIGOLENSPALTEN}
        zeilen={daten.bauwerke}
        schluessel={(b) => b.id}
        zeilenKlasse={(b) => deckung(zahlfeld(b.werte, 'DeckungModellProzent')).klasse}
        leer={<EmptyState klein titel="Der Lauf trägt kein Bauwerk." />}
        dicht
      />

      {daten.hinweise && daten.hinweise.length > 0 && (
        <ul className="aus-hinweis">
          {daten.hinweise.map((h, i) => <li key={i}>{h}</li>)}
        </ul>
      )}
    </ContentSection>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Hydranten
// ═══════════════════════════════════════════════════════════════════════════

const HYDRANTENSPALTEN: Spalte<AussenNachweisHydrant>[] = [
  { schluessel: 'bezeichnung', titel: 'Hydrant', immerSichtbar: true, wert: (h) => h.bezeichnung },
  {
    schluessel: 'nachbar', titel: 'Nachbar', einheit: 'm', art: 'zahl', nachkomma: 1,
    wert: (h) => zahlfeld(h.werte, 'NachbarabstandM'),
  },
  {
    schluessel: 'entfernung', titel: 'Entfernung', einheit: 'm', art: 'zahl', nachkomma: 1,
    wert: (h) => zahlfeld(h.werte, 'EntfernungM'),
  },
  {
    schluessel: 'druckverlust', titel: 'Δp', einheit: 'bar', art: 'zahl', nachkomma: 3,
    wert: (h) => zahlfeld(h.werte, 'DruckverlustBar'),
  },
  {
    schluessel: 'geschwindigkeit', titel: 'v', einheit: 'm/s', art: 'zahl', nachkomma: 2,
    wert: (h) => zahlfeld(h.werte, 'GeschwindigkeitMs'),
  },
  {
    schluessel: 'bewertung', titel: 'Bewertung', art: 'status',
    wert: (h) => bewertungStatus(h.bewertung).text, inhalt: (h) => marke(h.bewertung),
  },
  { schluessel: 'befund', titel: 'Befund', inhalt: (h) => <span className="aus-fein">{h.befund ?? '—'}</span> },
];

/**
 * Die Lage der Hydranten mit dem Abstand zum nächsten Nachbarn.
 *
 * Eine Karte und keine Liste, weil der Regelabstand nach DVGW W 331 eine
 * Aussage über die VERTEILUNG ist: Elf Hydranten, die alle an einer Ecke
 * stehen, erfüllen jede Abstandsliste und keine Verteilung. Dieselben Werte
 * stehen darunter als Tabelle (S1.10: kein Bild ohne seine Werte).
 *
 * Der Maßstab bleibt weg. Die Koordinaten sind Modellkoordinaten in
 * Millimetern, und eine Karte mit Maßstabsangabe, die keine Nordrichtung
 * kennt, behauptet mehr, als sie weiß.
 */
function Hydrantenkarte({ daten }: { daten: AussenNachweisHydranten }) {
  const punkte = daten.hydranten
    .map((h) => ({
      h,
      x: zahlfeld(h.werte, 'LageX'),
      y: zahlfeld(h.werte, 'LageY'),
      abstand: zahlfeld(h.werte, 'NachbarabstandM'),
    }))
    .filter((p): p is { h: AussenNachweisHydrant; x: number; y: number; abstand: number | null } =>
      p.x !== null && p.y !== null);

  const xs = punkte.map((p) => p.x);
  const ys = punkte.map((p) => p.y);

  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  const yMin = Math.min(...ys), yMax = Math.max(...ys);

  const breite = Math.max(xMax - xMin, 1);
  const hoehe = Math.max(yMax - yMin, 1);

  const rand = 24;
  const flaeche = 520;

  // Gleicher Maßstab in beiden Richtungen: Eine gestreckte Karte macht aus
  // einer Reihe eine Fläche.
  const massstab = (flaeche - 2 * rand) / Math.max(breite, hoehe);

  const beanstandet = punkte.filter((p) => p.h.bewertung !== 'ok').length;

  return (
    <>
      <ContentSection
        titel={`Hydranten (${daten.hydranten.length})`}
        beschreibung={daten.norm ?? undefined}
      >
        {punkte.length > 1 ? (
          <svg
            className="aus-karte"
            viewBox={`0 0 ${flaeche} ${flaeche}`}
            role="img"
            aria-label={`Lage von ${punkte.length} Hydranten mit dem Abstand zum nächsten Nachbarn, `
              + `${beanstandet} davon nicht in Ordnung. Die Werte stehen in der Tabelle „Werte als Tabelle“.`}
          >
            {punkte.map((p) => {
              const cx = rand + (p.x - xMin) * massstab;
              // Y nach OBEN: In SVG wächst y nach unten, im Modell nach
              // Norden. Ohne die Spiegelung stünde die Karte auf dem Kopf.
              const cy = flaeche - rand - (p.y - yMin) * massstab;

              return (
                <g key={p.h.id}>
                  <circle className="aus-karte-punkt" data-zustand={bewertungStatus(p.h.bewertung).zustand}
                    cx={cx} cy={cy} r={7} />
                  <text className="aus-karte-text" x={cx + 11} y={cy + 4}>
                    {p.h.bezeichnung ?? '—'}
                    {p.abstand !== null && ` · ${formatiereZahl(p.abstand, 1)} m`}
                  </text>
                </g>
              );
            })}
          </svg>
        ) : (
          <EmptyState klein titel="Zu wenige Hydranten mit Koordinaten für eine Karte."
            erklaerung="Die Werte stehen in der Tabelle darunter." />
        )}
      </ContentSection>

      <ContentSection titel="Werte als Tabelle" aufklappbar>
        <DataTable<AussenNachweisHydrant>
          name="Hydranten"
          spalten={HYDRANTENSPALTEN}
          zeilen={daten.hydranten}
          schluessel={(h) => h.id}
          leer={<EmptyState klein titel="Der Lauf trägt keinen Hydranten." />}
          dicht
        />
      </ContentSection>
    </>
  );
}
